import type { ReactNode } from 'react';
import { useI18n } from '../i18n/index.tsx';
import type { MessageKey } from '../i18n/messages.ts';
import { useExpertMode } from '../expert/index.tsx';
import { useCan } from '../session/index.tsx';
import { Badge, type Tone } from './ui.tsx';
import './operational.css';

/**
 * The pieces the operational screens share.
 *
 * All four exist to stop the same sentence being written differently on three
 * screens. "Working", "healthy" and "OK" are one state, and a customer who
 * sees all three words for it has to work out whether they mean the same
 * thing.
 */

const HEALTH: Record<string, { key: MessageKey; tone: Tone }> = {
  healthy: { key: 'health.healthy', tone: 'success' },
  degraded: { key: 'health.degraded', tone: 'warning' },
  unhealthy: { key: 'health.unhealthy', tone: 'danger' },
  unknown: { key: 'health.unknown', tone: 'neutral' },
};

/**
 * A health status, in one word.
 *
 * An unrecognised status renders as "not checked yet" rather than as its raw
 * value: a new status string added on the server should look like something
 * nobody has established, not like an error code leaking into the interface.
 */
export function HealthBadge({ status }: { status: string }): ReactNode {
  const { t } = useI18n();
  const entry = HEALTH[status] ?? HEALTH['unknown']!;
  return <Badge tone={entry.tone}>{t(entry.key)}</Badge>;
}

export function healthTone(status: string): Tone {
  return (HEALTH[status] ?? HEALTH['unknown']!).tone;
}

/**
 * When something last happened, or that it never has.
 *
 * `null` is its own answer here. A dash would read as a formatting gap; "never"
 * is a fact, and for a last-health-check it is the most important one on the
 * screen.
 */
export function When({ value }: { value: string | null | undefined }): ReactNode {
  const { t, formatDateTime } = useI18n();
  if (!value) return <span className="muted">{t('common.never')}</span>;
  return <time dateTime={value}>{formatDateTime(value)}</time>;
}

/**
 * How much of something is in use.
 *
 * Shows the figures as well as the bar. A bar alone is a shape; "3.1 of 3.8 GB
 * used" is what a person needs to decide whether to do anything, and it is
 * still readable when the bar is not (small screen, high contrast, print).
 */
export function Meter({
  label,
  used,
  total,
  format,
}: {
  label: string;
  used: number;
  total: number;
  format: (value: number) => string;
}): ReactNode {
  const { t } = useI18n();
  // A total of zero is not 100% full, it is unknown. Dividing by it would say
  // the opposite as confidently as possible.
  const fraction = total > 0 ? Math.min(1, Math.max(0, used / total)) : 0;
  const percent = Math.round(fraction * 100);
  const tone = percent >= 90 ? 'danger' : percent >= 75 ? 'warning' : 'ok';

  return (
    <div className="meter">
      <div className="meter__head">
        <span>{label}</span>
        <span className="muted">
          {total > 0
            ? t('monitoring.usedOf', { used: format(used), total: format(total) })
            : t('common.unknown')}
        </span>
      </div>
      <div
        className="meter__track"
        role="meter"
        aria-label={label}
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <span
          className={`meter__fill meter__fill--${tone}`}
          style={{ inlineSize: `${percent}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Detail that only appears when somebody asked for it.
 *
 * Renders nothing at all outside expert mode — not hidden with CSS, not
 * collapsed. The content is usually identifiers and measurements, and a
 * customer scrolling past a collapsed "technical details" row on every card is
 * being shown the machinery whether they open it or not.
 *
 * This is presentation only. Diagnostics the account is not entitled to never
 * arrive from the server, so there is nothing here for a toggle to reveal.
 */
export function Technical({ children }: { children: ReactNode }): ReactNode {
  const { t } = useI18n();
  const { expert } = useExpertMode();
  if (!expert) return null;
  return (
    <div className="technical">
      <span className="technical__label">{t('common.technical')}</span>
      <div className="technical__body">{children}</div>
    </div>
  );
}

/**
 * Whether this account may take an action, and what to say when it may not.
 *
 * Returns the reason rather than hiding the control, because the repository
 * already holds that rule for the wizard's options — "an option ruled out by
 * an earlier choice is shown greyed with the reason, never removed; a missing
 * option is a mystery" — and it is more true here, not less: a support user
 * who cannot find the restore button has no way to learn that the button
 * exists and their role is the reason.
 *
 * The check is a courtesy. The API enforces the same permission on every
 * request, and nothing here can grant one (`session/index.tsx`).
 */
export function usePermission(permission: string): {
  readonly allowed: boolean;
  readonly reason: string | undefined;
} {
  const { t } = useI18n();
  const allowed = useCan(permission);
  return { allowed, reason: allowed ? undefined : t('permission.cannot') };
}
